import { useState, useEffect, useMemo } from 'react';
import { Search, Check, X } from 'lucide-react';
import type { FilterFieldDef, FilterOption } from '../schema';
import type { ActiveFilter } from '../parser';
import { useI18n } from '@/context/I18nContext';

interface EnumFilterEditorProps {
  fieldDef: FilterFieldDef;
  filter?: ActiveFilter;
  onApply: (selectedVals: string[]) => void;
  onClose: () => void;
}

export function EnumFilterEditor({
  fieldDef,
  filter,
  onApply,
  onClose,
}: EnumFilterEditorProps) {
  const { t } = useI18n();
  const allowMultiple = fieldDef.allowMultiple !== false;
  const initialSelected = useMemo(() => {
    if (filter?.values && filter.values.length > 0) return filter.values;
    if (filter?.value) return [filter.value];
    return [];
  }, [filter]);

  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<FilterOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!fieldDef.options) return;
    if (typeof fieldDef.options === 'function') {
      setLoading(true);
      fieldDef
        .options()
        .then((res) => setOptions(res))
        .catch(() => setOptions([]))
        .finally(() => setLoading(false));
    } else {
      setOptions(fieldDef.options);
    }
  }, [fieldDef]);

  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const lower = search.toLowerCase();
    return options.filter(
      (opt) =>
        t(opt.label as any).toLowerCase().includes(lower) ||
        opt.value.toLowerCase().includes(lower)
    );
  }, [options, search, t]);

  const toggleOption = (val: string) => {
    if (!allowMultiple) {
      setSelected([val]);
      onApply([val]);
      onClose();
      return;
    }

    setSelected((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  const handleApply = () => {
    onApply(selected);
    onClose();
  };

  return (
    <div className="space-y-3">
      {/* Search Filter for Options */}
      {options.length > 4 && (
        <div className="relative">
          <Search className="w-4 h-4 sm:w-3.5 sm:h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('filter.searchOptions')}
            className="w-full pl-9 pr-8 py-2 sm:py-1.5 bg-bg-surface border border-border-default rounded-lg text-sm sm:text-xs text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-primary"
            >
              <X size={13} />
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="text-xs text-text-muted text-center py-4">
          {t('filter.loadingOptions')}
        </div>
      )}

      {/* Options List */}
      <div className="max-h-[50dvh] sm:max-h-52 overflow-y-auto space-y-1 pr-1">
        {filteredOptions.map((opt) => {
          const isChecked = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleOption(opt.value)}
              className={`w-full flex items-center justify-between p-2.5 sm:p-2 rounded-lg text-sm sm:text-xs text-left transition-colors cursor-pointer ${
                isChecked
                  ? 'bg-accent/15 text-accent font-medium border border-accent/30'
                  : 'bg-bg-surface/50 border border-transparent text-text-secondary hover:text-text-primary hover:bg-bg-hover'
              }`}
            >
              <div className="flex items-center gap-2.5 truncate">
                <div
                  className={`w-4 h-4 sm:w-3.5 sm:h-3.5 rounded flex items-center justify-center border transition-colors shrink-0 ${
                    isChecked
                      ? 'bg-accent border-accent text-white'
                      : 'border-border-default bg-bg-surface'
                  }`}
                >
                  {isChecked && <Check className="w-3 h-3 sm:w-2.5 sm:h-2.5 stroke-[3]" />}
                </div>
                {opt.icon && <opt.icon className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-text-muted" />}
                <span className="truncate">{t(opt.label as any)}</span>
              </div>
              {opt.badge && (
                <span className="text-[0.7rem] px-2 py-0.5 rounded bg-bg-surface text-text-muted font-mono ml-2 shrink-0">
                  {opt.badge}
                </span>
              )}
            </button>
          );
        })}
        {!loading && filteredOptions.length === 0 && (
          <div className="text-xs text-text-muted text-center py-4">
            {t('filter.noOptions')}
          </div>
        )}
      </div>

      {/* Action Footer */}
      {allowMultiple && (
        <div className="flex items-center justify-between pt-3 border-t border-border-subtle">
          <button
            type="button"
            onClick={() => setSelected([])}
            className="text-xs text-text-muted hover:text-text-secondary transition-colors cursor-pointer py-1 px-2 rounded"
          >
            {t('filter.clear')}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 sm:py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-primary hover:bg-bg-hover transition-colors cursor-pointer"
            >
              {t('filter.cancel')}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="px-4 py-2 sm:py-1.5 rounded-lg text-xs font-medium bg-accent text-white hover:bg-accent-hover transition-colors cursor-pointer shadow-sm"
            >
              {t('filter.apply')} {selected.length > 0 && `(${selected.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
